import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Base64 } from "js-base64";

import { RevisionFileAction } from "types/generated";
import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import CodeEditor from "components/CodeEditor";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Button from "ds/components/Button";
import useErrorHandle from "hooks/useErrorHandle";
import useSearchSpaceliftVcsFiles from "shared/SpaceliftVcsRepo/useSearchSpaceliftVcsFiles";
import useGetSpaceliftVcsRepo from "shared/SpaceliftVcsRepo/useGetSpaceliftVcsRepo";
import { getRepoUrl, REPOS_PAGE_URL } from "utils/spaceliftRepo";
import { getLanguageByFileName } from "components/CodeEditor/helpers";
import { makeStringEqualsPredicate } from "utils/predicates";

import {
  buildFolderBreadcrumbs,
  buildPathSearch,
  decodeFileContent,
  parseFilePath,
  SPACELIFT_VCS_REPO_SORT_STORAGE_KEY,
} from "../helpers";
import SpaceliftVcsRepoCommitDialog, { CommitArgs } from "../CommitDialog";
import useRepoCommit from "../useRepoCommit";
import useFilesNavigation from "../Files/Table/useFilesNavigation";
import SpaceliftVcsRepoBinaryFilePlaceholder from "../BinaryFilePlaceholder";

const SpaceliftVcsRepoEditFile = () => {
  const { repoId, fileName } = useParams<{ repoId: string; fileName: string }>();
  const navigate = useNavigate();

  const { folderPathSegments, folderPath } = useFilesNavigation();
  const filePathFromParams = folderPath ? `${folderPath}/${fileName}` : fileName!;

  const { repo, loading: repoLoading, error: repoError } = useGetSpaceliftVcsRepo({ repoId });

  const predicates = useMemo(() => {
    return [makeStringEqualsPredicate("filePath", [filePathFromParams])];
  }, [filePathFromParams]);

  const {
    files,
    loading: filesLoading,
    error: filesError,
  } = useSearchSpaceliftVcsFiles({ repoId, predicates });
  const { commit, loading: commitLoading } = useRepoCommit();

  const file = files[0] as (typeof files)[number] | undefined;

  const [content, setContent] = useState<string | null>(null);
  const [isCommitDialogOpen, setIsCommitDialogOpen] = useState(false);

  const decoded = useMemo(
    () => decodeFileContent(file?.content?.content),
    [file?.content?.content]
  );

  const originalContent = decoded.content ?? "";
  const displayContent = content ?? originalContent;
  const hasChanges = content !== null && content !== originalContent;

  const repoUrl = getRepoUrl(repoId!);

  useBreadcrumbs(
    [
      {
        title: "Repos",
        link: getBreadcrumbsBackUrl(REPOS_PAGE_URL, SPACELIFT_VCS_REPO_SORT_STORAGE_KEY),
      },
      {
        title: repo?.name || "",
        link: repoUrl,
      },
      ...buildFolderBreadcrumbs(repoUrl, folderPathSegments),
      {
        title: "Edit file",
      },
    ],
    [repo?.name, repoUrl, folderPathSegments]
  );

  const handleCancel = () => {
    navigate(`${repoUrl}${buildPathSearch(folderPath)}`);
  };

  const handleCommit = ({
    filePath: newPath = "",
    commitMessage,
    commitDescription,
  }: CommitArgs) => {
    const isRenamed = newPath !== filePathFromParams;

    const files = [
      {
        path: newPath,
        content: Base64.encode(displayContent),
        encrypt: null,
        fileMode: null,
        action: isRenamed ? RevisionFileAction.Create : RevisionFileAction.Update,
      },
      ...(isRenamed
        ? [
            {
              path: filePathFromParams,
              content: null,
              encrypt: null,
              fileMode: null,
              action: RevisionFileAction.Delete,
            },
          ]
        : []),
    ];

    commit(files, commitMessage, commitDescription, () => {
      setIsCommitDialogOpen(false);
      const { folder: newFolder } = parseFilePath(newPath);
      const search = buildPathSearch(newFolder);
      navigate(`${repoUrl}${search}`);
    });
  };

  const error = repoError || filesError;
  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if ((repoLoading || filesLoading) && !repo) {
    return <PageLoading />;
  }

  if (!repo || (!filesLoading && !file)) {
    return <NotFoundPage />;
  }

  return (
    <>
      <ViewHeader>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>{fileName}</ViewHeaderTitle>
          </ViewHeaderWrapper>

          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <Button variant="secondary" onPress={handleCancel}>
              Cancel
            </Button>
            {!decoded.isBinary && (
              <Button
                variant="primary"
                onPress={() => setIsCommitDialogOpen(true)}
                disabled={!hasChanges}
              >
                Commit changes
              </Button>
            )}
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>
      </ViewHeader>

      {decoded.isBinary ? (
        <SpaceliftVcsRepoBinaryFilePlaceholder fileType={decoded.fileType} />
      ) : (
        <CodeEditor
          body={displayContent}
          onChange={(value) => setContent(value || "")}
          language={getLanguageByFileName(fileName!)}
        />
      )}

      <SpaceliftVcsRepoCommitDialog
        variant="edit"
        isOpen={isCommitDialogOpen}
        onClose={() => setIsCommitDialogOpen(false)}
        onCommit={handleCommit}
        loading={commitLoading}
        filePath={filePathFromParams}
      />
    </>
  );
};

export default SpaceliftVcsRepoEditFile;
